// PACKAGES
import React, { useState, useEffect } from "react";
import { useLocation, Redirect } from "react-router-dom";
import { connect } from "react-redux";
import qs from "query-string";

// COMPONENTS
import Translated from "../../components/Translated/Translated";
import ProductGroupsDialog from "../../components/ProductGroupsDialog/ProductGroupsDialog";
import AddNonStockDialog from "../../components/AddNonStockDialog/AddNonStockDialog";
import StandardDialogBox from "../../components/StandardDialogBox/StandardDialogBox";
import ProductCard from "../../components/product-card/ProductCard2";

// FILES
import useLoader from "../../hooks/useLoader";
import api from "../../utils/api";
import {displayError} from "../../utils/toastHelper";
import * as actionTypes from "../../store/actions";
import translator from "../../utils/translator";
import checkSettings from "../../utils/checkSettings";
import paginate_styles from "../../styles/modules/Pagination.module.scss";
import "./ProductShopping.scss";

const ProductShopping = (props) => {
    const {
        // from state
        selectedGroupMap,
        roomNames,
        accountId,
        projectId,
        roomIds,
        selected_language,
        updateSelectedGroups,
        saveWarehouseNumberForQuoteScreen,
        stopDisplayingQuoteNavButton,
        display_quote_link_on_nav_bar,
        displayProductShoppingButtonOnNavBar,
        displayCheckPricesButtonOnNavBar,
    } = props;

    const searchLimit = 24;
    const shouldRedirectToHome = roomNames.length === 0;
    const location = useLocation();
    const [isLoading, load] = useLoader();
    // GET variables
    const { warehouseNumber } = qs.parse(location.search, { ignoreQueryPrefix: true });

    const [standardsOn, setStandardsOn] = useState(false);
    const [products, setProducts] = useState([]);
    const [nonStockOn, setNonStockOn] = useState(false);
    const [nonStockProducts, setNonStockProducts] = useState([]);
    const [inventoryOn, setInventoryOn] = useState(false);
    const [inStockOnlyOn, setInStockOnlyOn] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [userFiles, setUserFiles] = useState({});

    const pageStart = (page - 1) * searchLimit;
    const pageEnd = pageStart + products.length;
    const displayGroupProducts = Object.values(selectedGroupMap).flat().filter(
        (prod) => !inStockOnlyOn || prod.inventory // filter by inventory if needed
    );
    const displayProducts = nonStockOn
        ? [...nonStockProducts, ...displayGroupProducts, ...products]
        : [...displayGroupProducts, ...products];

    const hideModelNumber = checkSettings("hide_model_number");
    const hideStandardPricing = standardsOn && checkSettings("hide_standard_pricing");

    useEffect(
        () => {
            displayProductShoppingButtonOnNavBar(false, warehouseNumber);
            saveWarehouseNumberForQuoteScreen(warehouseNumber);
            display_quote_link_on_nav_bar(true);
            displayCheckPricesButtonOnNavBar(true);

            if (!shouldRedirectToHome) {
                load(
                    fetchShowStandards(),
                    fetchNonStock(),
                    fetchUserFiles()
                );
            }

            return () => {
                stopDisplayingQuoteNavButton();
                updateSelectedGroups({});
            };
        },
        []
    );

    useEffect(
        () => {
            if (inventoryOn) load(search());
            else if (inStockOnlyOn) setInStockOnlyOn(false);
        },
        [inventoryOn]
    );

    useEffect(
        () => {
            if (inStockOnlyOn && !inventoryOn) setInventoryOn(true);
            else load(search());
        },
        [inStockOnlyOn]
    );

    useEffect(
        () => {
            load(search());
        },
        [page]
    );

    const fetchShowStandards = async () => {
        try {
            const {
                project_pricing,
                is_project_acting_as_an_agent,
            } = await api.post(
				"/settings/account-pricing",
                { projectID: projectId },
			);
            setStandardsOn(!!(
                project_pricing &&
                project_pricing.allowance_type === 3 &&
                is_project_acting_as_an_agent
            ));
        } catch(error) {
			displayError(error, "Error fetching standards");
        }
    };

    const fetchNonStock = async () => {
        try {
            const data = await api.post(
                "/products/get-non-stock2",
                { projectId },
            );
            setNonStockProducts(data);
        } catch(error) {
			displayError(error, "Error fetching non stock");
        }
    };

    const fetchUserFiles = async () => {
        try {
            const data = await api.post(
                "/products/get-user-files",
                { projectId },
            );
            setUserFiles(data.products);
        } catch(error) {
			displayError(error, "Error fetching wishlist");
        }
    };

    const updateUserFile = (sku, field, file) => {
        const skuFileMap = userFiles[sku] ? userFiles[sku] : {};
        setUserFiles({
            ...userFiles,
            [sku]: {
                ...skuFileMap,
                [field]: file,
            },
        });
    };

    const search = async () => {
        // Exit if empty searchTerm
        if (!searchTerm) return;
        try {
            const searchData = await api.post(
                "/products/search-beta",
                {
                    query: searchTerm,
                    limit: searchLimit,
                    page,
                    language: selected_language,
                    filterInStock: inventoryOn && inStockOnlyOn,
                    showInventory: inventoryOn,
                    warehouseId: warehouseNumber,
                }
            );
            setProducts(searchData.products);
            setHasNextPage(searchData.hasNextPage);
        } catch (error) {
            displayError(error, "Error searching product");
        }
    };

    const searchOnEnter = (event) => {
        const enterKeyCode = 13;
        const isEnterPressed = (event.keyCode || event.which) === enterKeyCode;
        if (isEnterPressed && !isLoading) {
            if (page !== 1) setPage(1); // will trigger search
            else load(search());
        }
    };

    const ShoppingControls = (
        <div class="product-shopping-controls">
            <div class="product-shopping-checkbox-wrapper">
                <div class="form-check">
                    <input
                        onChange={() => setNonStockOn(!nonStockOn)}
                        type="checkbox"
                        class="form-check-input"
                        id="show_non_stocks"
                    />
                    <label class="form-check-label" for="show_non_stocks">
                        <Translated section="Product Shopping" label="show_non_stocks_label" />
                    </label>
                </div>
                <div class="form-check">
                    <input
                        onChange={() => setInventoryOn(!inventoryOn)}
                        checked={inventoryOn}
                        type="checkbox"
                        class="form-check-input"
                        id="display_inventory"
                    />
                    <label class="form-check-label" for="display_inventory">
                        <Translated section="Product Shopping" label="display_inventory_label" />
                    </label>
                </div>
                <div class="form-check">
                    <input
                        onChange={() => setInStockOnlyOn(!inStockOnlyOn)}
                        checked={inStockOnlyOn}
                        type="checkbox"
                        class="form-check-input"
                        id="in_stock_only"
                    />
                    <label class="form-check-label" for="in_stock_only">
                        <Translated section="Product Shopping" label="in_stock_label" />
                    </label>
                </div>
            </div>
            <div class="product-shopping-button-wrapper">
                {warehouseNumber ? (<ProductGroupsDialog warehouseNumber={warehouseNumber} />) : ""}
                <AddNonStockDialog
                    roomIds={roomIds}
                    roomNames={roomNames}
                    projectId={projectId}
                    accountId={accountId}
                    reload={fetchNonStock}
                />
                {standardsOn ? <StandardDialogBox projectId={projectId}/> : ""}
            </div>
        </div>
    );

    const SearchInput = (
        <div style={{display: "flex", justifyContent: "center"}}>
            <input
                onChange={event => setSearchTerm(event.target.value)}
                class="form-control product-shopping-search-input"
                type="text"
                placeholder={translator("Product Shopping", "search_term_label")}
                aria-label="Search"
                onKeyPress={searchOnEnter}
            />
            <button
                type="button"
                class="btn btn-outline-primary product-shopping-search-button"
                onClick={() => load(search())}
                disabled={!searchTerm}
            >
                {isLoading
                    ? <i class="fa fa-spinner fa-spin"></i>
                    : <i class="fa fa-search" />}
            </button>
        </div>
    );

    const Pagination = (
        <div className={paginate_styles.results_wrapper}>
            <div className={paginate_styles.results}>
                {products.length
                    ? `Page ${page} ${translator("Search Projects", "result_pc_label")} ${pageStart} - ${pageEnd}`
                    : translator("Product Shopping", "search_no_results_message")
                }
            </div>
            <div className={paginate_styles.pagination_wrapper}>
                <button
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    <i class="far fa-arrow-alt-circle-left" />
                </button>
                <button
                    disabled={inStockOnlyOn || !hasNextPage}
                    onClick={() => setPage(page + 1)}
                >
                    <i class="far fa-arrow-alt-circle-right" />
                </button>
            </div>
        </div>
    );

    const SearchList = (
        <div className="product-search-list">
            {displayProducts.map((product) => (
                <ProductCard
                    key={product.sku || ("nonstock" + product.id)}
                    product={product}
                    userFiles={product.sku ? userFiles[product.sku] || {} : null}
                    updateUserFile={updateUserFile}
                    updateNonStock={fetchNonStock}
                    fetchUserFiles={fetchUserFiles}
                    inventoryOn={inventoryOn}
                    standardsOn={standardsOn}
                    hideModelNumber={hideModelNumber}
                    hideStandardPricing={hideStandardPricing}
                    projectId={projectId}
                />
            ))}
        </div>
    );

    if (shouldRedirectToHome) {
        return <Redirect to="/search-projects" />;
    }

    return (
        <div class="product-shopping-page">
            <div class="product-shopping-header">
                <h1 class="product-shopping-title">
                    <Translated section="Product Shopping" label="page_title" />
                </h1>
                {ShoppingControls}
            </div>

            <p class="product-shopping-search-label">
                {translator("Product Shopping", "start_shopping_label")}
            </p>
            {SearchInput}
            {searchTerm ? (Pagination) : ""}
            <hr />
            {SearchList}
        </div>
    );
};

const mapStateToProps = state => ({
	selectedGroupMap: state.selectedGroupMap,
	roomNames: state.roomNames,
	accountId: state.accountIdForTheProjectYouAreShoppingFor,
	projectId: state.projectIdUsedToIncreaseProductQuantity,
	roomIds: state.roomIds,
	selected_language: state.selected_language,
});

const mapDispatchToProps = dispatch => {
	return {
        updateSelectedGroups: (updatedSelectedGroupMap) => dispatch({
			type: actionTypes.UPDATE_SELECTED_GROUPS,
			updatedSelectedGroupMap,
		}),
		saveWarehouseNumberForQuoteScreen: warehouseNumber =>
			dispatch({
				type: actionTypes.SAVE_WAREHOUSE_NUMBER_FOR_QUOTE_SCREEN,
				warehouseNumber: warehouseNumber
			}),
		stopDisplayingQuoteNavButton: () =>
			dispatch({
				type: actionTypes.STOP_DISPLAYING_QUOTE_NAV_BUTOTN
			}),
		display_quote_link_on_nav_bar: displayOrNot =>
			dispatch({
				type: actionTypes.DISPLAY_QUOTE_LINK_ON_NAV_BAR,
				displayOrNot
			}),
		displayProductShoppingButtonOnNavBar: (
			displayOrNot,
			selected_project_warehouse_number = "warehouse_number_has_not_been_provided_for_this_project"
		) =>
			dispatch({
				type: actionTypes.DISPLAY_PRODUCT_SHOPPING_BUTTON_ON_NAV_BAR,
				displayOrNot,
				selected_project_warehouse_number
			}),
		displayCheckPricesButtonOnNavBar: displayOrNot =>
			dispatch({
				type: actionTypes.DISPLAY_CHECK_PRICES_BUTTON_ON_NAV_BAR,
				displayOrNot
			})
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductShopping);
